import type { CharacterDraft } from '../types';

/**
 * Persistenza del draft del builder.
 *
 * Il draft è uno stato effimero della feature `character-builder` (sopravvive
 * al refresh ma viene cancellato al finalize). Per questo vive qui dentro
 * la feature e NON in `services/characterStorage`, che è dedicato al
 * Character finalizzato (un'entità di dominio condivisa).
 *
 * **Versionamento chiave**: `dwr.builder.draft.v2` — `v2` è il versioning
 * INTERNO della feature (formato di `CharacterDraft`), distinto da
 * `CHARACTER_SCHEMA_VERSION`. Se il formato del draft cambia (raro),
 * cambia la chiave. Il bump `v1 → v2` (PR Mago spells, 2026-05-09) è
 * stato necessario per l'introduzione del campo `spells: SpellRef[]` sul
 * draft: i draft `v1` salvati non hanno il campo, e accomodarli con un
 * default soft sarebbe stato fragile (tipi runtime divergerebbero dai
 * tipi statici). Il bump invalida i draft attivi pre-PR — accettabile per
 * un side-project con ricostruzione del PG in pochi minuti.
 */
const DRAFT_KEY = 'dwr.builder.draft.v2';

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

/**
 * Salva (o sovrascrive) il draft attivo.
 *
 * **Niente validazione Zod**: il draft è uno stato semi-strutturato per
 * definizione (campi opzionali). La consistenza è garantita dal reducer
 * che produce sempre `CharacterDraft` ben tipato.
 */
export function saveDraft(draft: CharacterDraft): void {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

/**
 * Carica il draft salvato. Restituisce `null` se assente o corrotto.
 *
 * **Tolleranza al corrotto**: a differenza del Character finalizzato (dove
 * un load fallito è grave), un draft corrotto è semplicemente "ricominciamo".
 * Catchiamo l'errore e restituiamo `null` invece di propagare.
 */
export function loadDraft(): CharacterDraft | null {
  const storage = getStorage();
  if (!storage) return null;
  const raw = storage.getItem(DRAFT_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    // Sanity check minimo: deve avere id e schemaVersion
    const draft = parsed as Partial<CharacterDraft>;
    if (typeof draft.id !== 'string' || draft.schemaVersion !== 2) return null;
    return parsed as CharacterDraft;
  } catch {
    return null;
  }
}

/**
 * Cancella il draft (al finalize del PG, o al "Ricomincia da capo").
 */
export function clearDraft(): void {
  const storage = getStorage();
  if (!storage) return;
  storage.removeItem(DRAFT_KEY);
}
